export type SseFaultMode = "netfail" | "status" | "buffered" | "errorFrame" | "drop" | "stall" | "truncate";

export interface SseFaultPlan {
  mode: SseFaultMode;
  code?: number;
  reason?: string | null;
  message?: string;
  afterFrames?: number;
  phase?: "connect" | "steady";
  remaining: number;
}

const isDev = import.meta.env.MODE !== "production";
const DEFAULT_ERROR_MESSAGE = "Something went wrong while processing your request. Please try again.";

let armed: SseFaultPlan | null = null;
let pendingStallMs: number | null = null;

function log(...args: any[]) {
  if (isDev) console.log("[sse-fault]", ...args);
}

export function takeSseFault(): SseFaultPlan | null {
  if (!isDev || !armed) return null;
  const plan = armed;
  armed = plan.remaining <= 1 ? null : { ...plan, remaining: plan.remaining - 1 };
  log("firing", plan.mode, {
    code: plan.code,
    reason: plan.reason,
    afterFrames: plan.afterFrames,
    phase: plan.phase,
  });
  return plan;
}

export function consumeSseStallMs(): number | null {
  if (!isDev) return null;
  const ms = pendingStallMs;
  pendingStallMs = null;
  return ms;
}

export function buildFaultErrorFrame(plan: SseFaultPlan): string {
  return JSON.stringify({
    type: "error",
    seq: 0,
    code: plan.code ?? 400,
    message: plan.message ?? DEFAULT_ERROR_MESSAGE,
    reason: plan.reason ?? null,
  });
}

function install() {
  if (!isDev || typeof window === "undefined") return;
  (window as any).__sseFault = {
    netfail: (times = 1) => {
      armed = { mode: "netfail", remaining: times };
      log("armed netfail", { times });
    },
    status: (code = 500, times = 1) => {
      armed = { mode: "status", code, remaining: times };
      log("armed status", { code, times });
    },
    buffered: (times = 1) => {
      armed = { mode: "buffered", remaining: times };
      log("armed buffered", { times });
    },
    error: (code = 400, reason: string | null = null, times = 1) => {
      armed = { mode: "errorFrame", code, reason, remaining: times };
      log("armed error", { code, reason, times });
    },
    emptyUser: (times = 1) => {
      armed = { mode: "errorFrame", code: 400, reason: "empty_user_message", remaining: times };
      log("armed emptyUser", { times });
    },
    drop: (afterFrames = 3, times = 1) => {
      armed = { mode: "drop", afterFrames, remaining: times };
      log("armed drop", { afterFrames, times });
    },
    truncate: (times = 1) => {
      armed = { mode: "truncate", remaining: times };
      log("armed truncate", { times });
    },
    stall: (ms = 3000, phase: "connect" | "steady" = "steady") => {
      pendingStallMs = ms;
      armed = { mode: "stall", phase, remaining: 1 };
      log("armed stall", { ms, phase });
    },
    clear: () => {
      armed = null;
      pendingStallMs = null;
      log("cleared");
    },
    state: () => ({ armed, pendingStallMs }),
    help: () => {
      console.log(
        [
          "window.__sseFault — dev SSE fault injector (arms one turn unless times>1)",
          "  .netfail(times=1)                       fetch throws before response (offline/DNS)",
          "  .status(code=500, times=1)              backend returns non-200",
          "  .buffered(times=1)                      non-stream response (streaming disabled upstream)",
          "  .error(code=400, reason=null, times=1)  synthetic xenia error frame",
          "  .emptyUser(times=1)                     error frame with reason='empty_user_message'",
          "  .drop(afterFrames=3, times=1)           die mid-stream after N real frames (reader throws)",
          "  .truncate(times=1)                      open then close with no terminal frame (silent mid-stream death)",
          "  .stall(ms=3000, phase='steady', times=1)  open then go silent -> stall watchdog fires",
          "  .clear()                                disarm everything",
          "  .state()                                inspect armed fault",
          "Arm one, run a turn (it faults), the next turn is normal -> proves Try again recovers.",
        ].join("\n"),
      );
    },
  };
  log("ready — window.__sseFault.help()");
}

if (import.meta.env.MODE !== "production") {
  install();
}
